import { validateProject, type TopControlProject } from './model';

/** Local browser backup only. Saving here never claims a server/QGIS synchronization. */
export class TopControlStore {
  private connection: Promise<IDBDatabase> | undefined;
  constructor(private factory: IDBFactory = indexedDB, private databaseName = 'geokontroll-topcontrol-v1') {}
  private open(): Promise<IDBDatabase> {
    if (!this.connection) this.connection = new Promise((resolve, reject) => {
      const request = this.factory.open(this.databaseName, 1);
      request.onupgradeneeded = () => { request.result.createObjectStore('projects', { keyPath: 'projectKey' }); };
      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => { db.close(); this.connection = undefined; };
        resolve(db);
      };
      request.onerror = () => { this.connection = undefined; reject(new Error('Kunne ikke åpne lokal Toppkontroll-lagring.')); };
      request.onblocked = () => { this.connection = undefined; reject(new Error('Lokal lagring er blokkert av en annen åpen fane.')); };
    });
    return this.connection;
  }
  async load(projectKey: string): Promise<TopControlProject | null> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('projects', 'readonly');
      const request = tx.objectStore('projects').get(projectKey);
      let result: TopControlProject | null = null;
      request.onsuccess = () => {
        try { result = request.result === undefined ? null : validateProject(request.result); }
        catch (error) { reject(error); }
      };
      tx.oncomplete = () => resolve(result);
      tx.onerror = () => reject(new Error('Kunne ikke lese lokal Toppkontroll.'));
      tx.onabort = () => reject(new Error('Lesing av lokal Toppkontroll ble avbrutt.'));
    });
  }
  /** Atomic optimistic revision check protects work when multiple viewer tabs are open. */
  async save(project: TopControlProject, expectedRevision = project.revision): Promise<TopControlProject> {
    const snapshot = validateProject(project);
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('projects', 'readwrite');
      const store = tx.objectStore('projects');
      const request = store.get(snapshot.projectKey);
      let error: Error | undefined;
      request.onsuccess = () => {
        const previous = request.result as TopControlProject | undefined;
        if ((previous?.revision ?? 0) !== expectedRevision || (previous && previous.id !== snapshot.id)) {
          error = new Error('Toppkontrollen er endret i en annen fane. Last inn lagret arbeid før du fortsetter.'); tx.abort(); return;
        }
        snapshot.revision = expectedRevision + 1;
        snapshot.updatedAt = new Date().toISOString();
        store.put(snapshot);
      };
      tx.oncomplete = () => resolve(snapshot);
      tx.onerror = () => reject(error ?? new Error('Kunne ikke lagre Toppkontroll lokalt. Eksporter en sikkerhetskopi før du lukker.'));
      tx.onabort = () => reject(error ?? new Error('Lagring ble avbrutt. Eksporter en sikkerhetskopi før du lukker.'));
    });
  }
  async close(): Promise<void> {
    if (this.connection) (await this.connection).close();
    this.connection = undefined;
  }
}
